import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Cloud, ArrowsClockwise, Warning, CaretDown } from "@phosphor-icons/react";
import { adsStatus, adsListAccounts, adsListCampaigns, errText } from "@/lib/api";

/**
 * A compact bar shown ABOVE the CSV upload zone in each auditor.
 * If Google Ads is connected, offers an account/date picker + Fetch button.
 * If not, shows a small hint pointing at the sidebar Connect button.
 *
 * Props:
 *   reportKind: "search-terms" | "geographic"
 *   onFetch: async (customer_id, date_range, account) => void
 */
const RANGES = [
    { key: "LAST_7_DAYS", label: "7d" },
    { key: "LAST_14_DAYS", label: "14d" },
    { key: "LAST_30_DAYS", label: "30d" },
    { key: "LAST_90_DAYS", label: "90d" },
    { key: "LAST_MONTH", label: "Last mo." },
    { key: "THIS_MONTH", label: "This mo." },
];

export const AdsImportBar = ({ reportKind, onFetch }) => {
    const [status, setStatus] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [customerId, setCustomerId] = useState("");
    const [dateRange, setDateRange] = useState("LAST_30_DAYS");
    const [fetching, setFetching] = useState(false);

    // Campaign picker (search-terms + auction-insights)
    const supportsCampaignFilter =
        reportKind === "search-terms" || reportKind === "auction-insights";
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [selectedCampaigns, setSelectedCampaigns] = useState(new Set()); // Set of ids
    const [campaignOpen, setCampaignOpen] = useState(false);

    useEffect(() => {
        adsStatus().then(setStatus).catch(() => setStatus({ configured: false, connected: false }));
    }, []);

    const loadAccounts = useCallback(async () => {
        setAccountsLoading(true);
        try {
            const data = await adsListAccounts();
            const list = data.accounts || [];
            setAccounts(list);
            if (list.length && !customerId) setCustomerId(list[0].id);
        } catch (e) {
            const msg = errText(e, "Failed to load accounts");
            toast.error(msg);
        } finally {
            setAccountsLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        if (status?.connected) loadAccounts();
    }, [status?.connected, loadAccounts]);

    // Load campaigns whenever the account changes.
    useEffect(() => {
        if (!supportsCampaignFilter || !customerId) return;
        let cancelled = false;
        setCampaignsLoading(true);
        setSelectedCampaigns(new Set());
        adsListCampaigns(customerId)
            .then((data) => {
                if (!cancelled) setCampaigns(data.campaigns || []);
            })
            .catch((e) => {
                if (!cancelled) {
                    setCampaigns([]);
                    const msg = errText(e, "Failed to load campaigns");
                    toast.error(msg);
                }
            })
            .finally(() => {
                if (!cancelled) setCampaignsLoading(false);
            });
        return () => { cancelled = true; };
    }, [customerId, supportsCampaignFilter]);

    const toggleCampaign = (id) =>
        setSelectedCampaigns((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });

    const handleFetch = async () => {
        if (!customerId) {
            toast.error("Pick a client account first");
            return;
        }
        const acct = accounts.find((a) => a.id === customerId);
        const campaignIds = Array.from(selectedCampaigns);
        setFetching(true);
        const scopeLabel = supportsCampaignFilter && campaignIds.length
            ? `${campaignIds.length} campaign${campaignIds.length === 1 ? "" : "s"}`
            : "the whole account";
        const toastId = toast.loading(
            `Fetching ${reportKind} for ${scopeLabel}…`
        );
        try {
            await onFetch(customerId, dateRange, acct, campaignIds);
            toast.success(`Loaded ${reportKind} for ${scopeLabel}`, { id: toastId });
        } catch (e) {
            const msg = errText(e, "Fetch failed");
            toast.error(msg, { id: toastId });
        } finally {
            setFetching(false);
        }
    };

    if (!status) return null;

    if (!status.configured) {
        return (
            <div className="mb-6 border border-neutral-300 bg-neutral-50 px-4 py-3 flex items-start gap-2 text-[11px] text-neutral-600">
                <Warning size={14} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
                <span>
                    Google Ads API not configured. Upload CSVs manually for now — or ask your admin to add the Google Ads credentials in backend .env.
                </span>
            </div>
        );
    }

    if (!status.connected) {
        return (
            <div className="mb-6 border border-neutral-300 bg-neutral-50 px-4 py-3 flex items-center gap-2 text-[11px] text-neutral-600">
                <Cloud size={14} weight="bold" className="text-klein" />
                <span>
                    Tip: click <span className="font-bold text-ink">Connect Google Ads</span> in the sidebar to pull reports directly instead of uploading CSVs.
                </span>
            </div>
        );
    }

    return (
        <div
            data-testid={`ads-import-bar-${reportKind}`}
            className="mb-6 border border-emerald-600 bg-emerald-50 px-4 py-3 flex flex-wrap items-center gap-3"
        >
            <div className="flex items-center gap-2 min-w-fit">
                <Cloud size={14} weight="bold" className="text-emerald-700" />
                <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-emerald-900">
                    Google Ads · MCC {status.mcc_id}
                </span>
            </div>

            <select
                data-testid="ads-account-select"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="flex-1 min-w-[200px] h-8 px-2 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none"
                disabled={accountsLoading || !accounts.length}
            >
                {accountsLoading ? (
                    <option>Loading accounts…</option>
                ) : accounts.length === 0 ? (
                    <option>No client accounts found</option>
                ) : (
                    accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                            {(a.name || "Unnamed")} · {a.id} {a.currency && `(${a.currency})`}
                        </option>
                    ))
                )}
            </select>

            {supportsCampaignFilter && (
                <div className="relative">
                    <button
                        data-testid="ads-campaign-picker"
                        onClick={() => setCampaignOpen((v) => !v)}
                        disabled={campaignsLoading || !campaigns.length}
                        className="h-8 px-2 text-xs font-mono bg-white border border-neutral-300 hover:border-ink text-ink flex items-center gap-1.5 disabled:opacity-50 min-w-[180px]"
                    >
                        {campaignsLoading ? (
                            <ArrowsClockwise size={11} weight="bold" className="animate-spin" />
                        ) : (
                            <span className="mono-label text-neutral-500">Campaigns:</span>
                        )}
                        <span className="font-bold truncate">
                            {campaignsLoading
                                ? "loading…"
                                : selectedCampaigns.size === 0
                                ? "All campaigns"
                                : `${selectedCampaigns.size} selected`}
                        </span>
                        <CaretDown size={10} weight="bold" className="ml-auto" />
                    </button>
                    {campaignOpen && campaigns.length > 0 && (
                        <div
                            data-testid="ads-campaign-dropdown"
                            className="absolute z-20 left-0 mt-1 bg-white border-2 border-ink shadow-lg w-[320px] max-h-72 overflow-y-auto"
                        >
                            <div className="sticky top-0 bg-neutral-50 border-b border-neutral-300 px-2 py-1.5 flex items-center justify-between">
                                <span className="mono-label">
                                    {selectedCampaigns.size} of {campaigns.length}
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() =>
                                            setSelectedCampaigns(
                                                new Set(campaigns.map((c) => c.id))
                                            )
                                        }
                                        className="text-[10px] font-mono uppercase tracking-widest text-klein hover:text-ink"
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => setSelectedCampaigns(new Set())}
                                        className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 hover:text-ink"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            {campaigns.map((c) => {
                                const on = selectedCampaigns.has(c.id);
                                return (
                                    <label
                                        key={c.id}
                                        data-testid={`ads-campaign-opt-${c.id}`}
                                        className="flex items-start gap-2 px-2 py-1.5 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-b-0"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={on}
                                            onChange={() => toggleCampaign(c.id)}
                                            className="mt-0.5 accent-klein"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-medium text-ink truncate">
                                                {c.name}
                                            </div>
                                            <div className="text-[10px] font-mono text-neutral-500">
                                                {c.channel} · {c.status}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center">
                {RANGES.map((r) => (
                    <button
                        key={r.key}
                        data-testid={`ads-range-${r.key}`}
                        onClick={() => setDateRange(r.key)}
                        className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold border transition ${
                            dateRange === r.key
                                ? "bg-ink text-white border-ink"
                                : "bg-white text-ink border-neutral-300 hover:border-ink"
                        }`}
                    >
                        {r.label}
                    </button>
                ))}
            </div>

            <button
                data-testid="btn-ads-fetch"
                onClick={handleFetch}
                disabled={fetching || !customerId || accountsLoading}
                className="brutal-btn border border-emerald-800 bg-emerald-700 text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-40"
            >
                {fetching ? (
                    <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
                ) : (
                    <Cloud size={13} weight="bold" />
                )}
                Fetch from Google Ads
            </button>
        </div>
    );
};
